/**
 * GET /v1/synced-events — list synced events for a school.
 *
 * Admin-gated, school-scoped. Phase 1 returns athletics only, projected
 * onto the source-agnostic `SchoolEvent` contract.
 *
 * See: docs/_archive/2026-10-17-plans-sweep/plans/2026-06-14-003-feat-synced-events-explorer-plan.md (U1)
 */

import { z } from 'zod'
import {
  SchoolEventAudienceScopeSchema,
  SchoolEventSchema,
  SchoolEventStatusSchema
} from '../../schemas/entities/school-event'

/**
 * Logical event sources the synced-events union can be filtered to. Subset of
 * `SchoolEventSourceSchema` — the spine reader covers three persisted sources
 * ('ical' has no projector yet).
 */
export const SyncedEventsSourceSchema = z.enum(['blackbaud_events', 'blackbaud_athletics', 'ticketed'])
export type SyncedEventsSource = z.infer<typeof SyncedEventsSourceSchema>

/**
 * Request query params for `GET /v1/synced-events`.
 *
 * Season filtering ships as raw `start_date`/`end_date` (per the plan's
 * U1 open question — simplest Phase-1 path; the web client presents a
 * "this season" preset range on top). All params are optional; no date
 * filter = return all rows for the school ordered by game date.
 *
 * `status` accepts a CSV string: `?status=cancelled,rescheduled`.
 */
export const ListSyncedEventsRequestSchema = z.object({
  /** ISO 8601 date string — lower bound on startDate (inclusive). */
  start_date: z.string().optional(),
  /** ISO 8601 date string — upper bound on startDate (inclusive). */
  end_date: z.string().optional(),
  /** Filter by team / sport name (case-insensitive substring match). Athletics only. */
  team: z.string().optional(),
  /** Filter by home/away value ("Home", "Away", "Neutral"). Athletics only. */
  home_away: z.string().optional(),
  /**
   * Restrict the union to a single source. Omitted = all three sources
   * (BB calendar events, athletics, ticketed). Source-neutral.
   */
  source: SyncedEventsSourceSchema.optional(),
  /**
   * CSV of audience-scope values: `school_wide`, `section`, `community_group`,
   * `activity`. Empty / missing = no scope filter. Source-neutral.
   */
  audience_scope: z
    .string()
    .optional()
    .transform(value =>
      value
        ? value
            .split(',')
            .map(s => s.trim().toLowerCase())
            .filter(Boolean)
        : undefined
    )
    .pipe(z.array(SchoolEventAudienceScopeSchema).optional()),
  /**
   * CSV of status values: `cancelled`, `rescheduled`, `scheduled`.
   * Empty / missing = no status filter (return all).
   */
  status: z
    .string()
    .optional()
    .transform(value =>
      value
        ? value
            .split(',')
            .map(s => s.trim().toLowerCase())
            .filter(Boolean)
        : undefined
    )
    .pipe(z.array(SchoolEventStatusSchema).optional()),
  /**
   * Maximum rows to return per page (default 100, max 2000).
   * Raised from 500 to cover large schools (Lovett has 922 rows) with headroom.
   * TODO: replace this one-shot fetch with real server-side pagination+filtering.
   */
  limit: z.coerce.number().int().positive().max(2000).optional(),
  /** Rows to skip for pagination (default 0). */
  offset: z.coerce.number().int().min(0).optional()
})
export type ListSyncedEventsRequest = z.infer<typeof ListSyncedEventsRequestSchema>

export const ListSyncedEventsResponseSchema = z.object({
  data: z.array(SchoolEventSchema),
  meta: z.object({
    total: z.number().int(),
    /** ISO 8601 string of the most recent sync, or null if school has no rows. */
    last_synced_at: z.string().nullable()
  })
})
export type ListSyncedEventsResponse = z.infer<typeof ListSyncedEventsResponseSchema>
